import { useState, useEffect, useRef, useContext, createContext } from "react";
import { createPortal } from "react-dom";
import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const CellSizeContext = createContext(28);

/* ============================================================
   LIVESTREAM PIXEL BOARD
   - Griglia con zone premium dorate al centro
   - Fusione pixel adiacenti in mega-blocchi
   - Video YouTube incorporato DIRETTAMENTE nel pixel (iframe)
   - Hover = autoplay muto in-place (nessun redirect esterno)
   - Persistenza + Realtime via Supabase (fallback demo se non configurato)
   ============================================================ */

const GRID_COLS = 25;
const GRID_ROWS = 20;
const PREMIUM_SIZE = 6; // quadrato centrale 6x6 di pixel premium
const PREMIUM_START_ROW = Math.floor((GRID_ROWS - PREMIUM_SIZE) / 2); // 7
const PREMIUM_START_COL = Math.floor((GRID_COLS - PREMIUM_SIZE) / 2); // 9
const PREMIUM_END_ROW = PREMIUM_START_ROW + PREMIUM_SIZE;             // 13 (esclusivo)
const PREMIUM_END_COL = PREMIUM_START_COL + PREMIUM_SIZE;             // 15 (esclusivo)

// Listino: prezzo unitario per pixel × tier di durata
// "hour" è il blocco base da 5 ore (€0.50 std → ≥ minimo Stripe per 1 px)
const PRICING = {
  standard: { hour: 0.50, day: 1.80, week: 9.90, month: 29.90 },
  premium:  { hour: 2.50, day: 7.90, week: 39.90, month: 99.00 },
};
const DURATIONS = [
  { key: "hour",  label: "5 Ore",        hours: 5 },
  { key: "day",   label: "1 Giorno",     hours: 24 },
  { key: "week",  label: "1 Settimana",  hours: 168 },
  { key: "month", label: "1 Mese",       hours: 720 },
];
const BASELINE_HOURS = 5;  // unità "5 ore" usata per calcolare il prezzo "non scontato" struck-through

const MIN_CELL = 22;   // dimensione minima pixel in px
const MAX_CELL = 48;   // dimensione massima pixel in px
const DEFAULT_CELL = 28;

const SOCIALS = {
  tiktok: {
    label: "TikTok",
    color: "#010101",
    svg: (
      <svg viewBox="0 0 24 24" fill="white" width="11" height="11">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.31 6.31 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.2 8.2 0 004.79 1.52V6.77a4.84 4.84 0 01-1.02-.08z" />
      </svg>
    ),
  },
  twitch: {
    label: "Twitch",
    color: "#9146FF",
    svg: (
      <svg viewBox="0 0 24 24" fill="white" width="11" height="11">
        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
      </svg>
    ),
  },
  youtube: {
    label: "YouTube",
    color: "#FF0000",
    svg: (
      <svg viewBox="0 0 24 24" fill="white" width="11" height="11">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
  instagram: {
    label: "Instagram",
    color: "#E1306C",
    gradient: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)",
    svg: (
      <svg viewBox="0 0 24 24" fill="white" width="11" height="11">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
  },
  kick: {
    label: "Kick",
    color: "#53fc18",
    svg: (
      <svg viewBox="0 0 24 24" fill="black" width="11" height="11">
        <path d="M3 3h5v4h2V5h2V3h5v4h-2v2h-2v6h2v2h2v4h-5v-2h-2v-2H8v4H3z" />
      </svg>
    ),
  },
  link: {
    label: "Link / Sito",
    color: "#0ea5e9",
    svg: (
      <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="11" height="11">
        <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 1 0-7.07-7.07l-1.5 1.5" />
        <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 1 0 7.07 7.07l1.5-1.5" />
      </svg>
    ),
  },
};

const RENT_OPTIONS_LEGACY = null; // legacy (sostituito da DURATIONS)

function isPremium(row, col) {
  return (
    row >= PREMIUM_START_ROW && row < PREMIUM_END_ROW &&
    col >= PREMIUM_START_COL && col < PREMIUM_END_COL
  );
}

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

function getTimeLeft(rentStart, durationHours) {
  if (!rentStart) return 0;
  const end = new Date(rentStart).getTime() + durationHours * 3600000;
  return Math.max(0, end - Date.now());
}

function formatTime(ms) {
  if (ms <= 0) return "00:00:00";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((x) => String(x).padStart(2, "0")).join(":");
}

/**
 * Costruisce le info di anteprima per QUALSIASI link.
 * Restituisce:
 *  - kind: 'iframe' (player video incorporabile) | 'card' (scheda con favicon/host)
 *  - iframeSrc, thumbnail, host, favicon, providerLabel
 *
 * Embed supportati: YouTube (watch/live/shorts/youtu.be), Twitch (canale), Kick (canale)
 * Fallback card: TikTok, Instagram, e qualsiasi link generico
 */
function getMediaEmbed(url, socialType) {
  if (!url) return { kind: "card", host: "", favicon: null, providerLabel: socialType };
  let u;
  try { u = new URL(url); } catch { return { kind: "card", host: url, favicon: null, providerLabel: socialType }; }
  const host = u.hostname.replace(/^www\./, "");
  const favicon = `https://www.google.com/s2/favicons?domain=${host}&sz=128`;

  // ───── YouTube ─────
  if (host === "youtube.com" || host === "youtu.be" || host.endsWith(".youtube.com")) {
    let videoId = null;
    if (host === "youtu.be") {
      videoId = u.pathname.slice(1).split("?")[0];
    } else if (u.searchParams.get("v")) {
      videoId = u.searchParams.get("v");
    } else {
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => ["live", "shorts", "embed"].includes(p));
      if (idx !== -1) videoId = parts[idx + 1];
    }
    if (videoId) {
      const params = new URLSearchParams({
        autoplay: "1", mute: "1", rel: "0", modestbranding: "1", controls: "1", playsinline: "1",
      });
      return {
        kind: "iframe",
        iframeSrc: `https://www.youtube.com/embed/${videoId}?${params.toString()}`,
        thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
        host, favicon, providerLabel: "YouTube",
      };
    }
  }

  // ───── Twitch ─────
  // Su mobile/iOS l'iframe Twitch spesso non carica per restrizioni parent/CORS.
  // Mostriamo SEMPRE una card brand-perfect (logo + viola Twitch + CTA "Guarda su Twitch")
  if (host === "twitch.tv" || host.endsWith(".twitch.tv")) {
    const parts = u.pathname.split("/").filter(Boolean);
    const channel = parts[0] && !["videos", "directory", "clip", "moderator"].includes(parts[0]) ? parts[0] : null;
    return {
      kind: "card",
      host, favicon,
      providerLabel: "Twitch",
      channel,
      brandColor: "#9146FF",
      brandGradient: "linear-gradient(135deg, #9146FF 0%, #6441A5 50%, #392e5c 100%)",
      ctaText: "GUARDA SU TWITCH",
      isTwitch: true,
    };
  }

  // ───── Kick ─────
  if (host === "kick.com" || host.endsWith(".kick.com")) {
    const parts = u.pathname.split("/").filter(Boolean);
    const channel = parts[0] && !["video", "videos", "clips"].includes(parts[0]) ? parts[0] : null;
    if (channel) {
      return {
        kind: "iframe",
        iframeSrc: `https://player.kick.com/${encodeURIComponent(channel)}?muted=true&autoplay=true`,
        thumbnail: null,
        host, favicon, providerLabel: "Kick", channel,
      };
    }
  }

  // ───── Fallback: scheda card (TikTok, Instagram, qualsiasi sito) ─────
  const providerMap = {
    "tiktok.com": "TikTok",
    "instagram.com": "Instagram",
    "x.com": "X / Twitter",
    "twitter.com": "X / Twitter",
    "facebook.com": "Facebook",
    "rumble.com": "Rumble",
    "vimeo.com": "Vimeo",
    "trovo.live": "Trovo",
  };
  const providerLabel = providerMap[host] || host;
  return { kind: "card", host, favicon, providerLabel };
}

// ─────────────────────────────────────────────────────────────

/* ── Dati demo (fallback se Supabase non è configurato) ───── */
function getDemoPixels() {
  const now = Date.now();
  const blocks = {};

  const twitchOwner = generateId();
  const twitchRoot = {
    id: generateId(), user_handle: "@neon_gamer", social_type: "twitch",
    media_url: "https://picsum.photos/seed/twitch1/200/200",
    live_link: "https://twitch.tv", color: "#9146FF",
    rent_start: new Date(now - 3600000).toISOString(), rent_duration_hours: 12,
    row: 2, col: 3, row_span: 2, col_span: 2, owner_id: twitchOwner,
  };
  blocks["2-3"] = twitchRoot;
  blocks["2-4"] = { owner_id: twitchOwner, ref: "2-3" };
  blocks["3-3"] = { owner_id: twitchOwner, ref: "2-3" };
  blocks["3-4"] = { owner_id: twitchOwner, ref: "2-3" };

  const tiktokOwner = generateId();
  blocks["0-10"] = {
    id: generateId(), user_handle: "@tikstar", social_type: "tiktok",
    media_url: "https://picsum.photos/seed/tiktok1/200/200",
    live_link: "https://tiktok.com", color: "#010101",
    rent_start: new Date(now - 1800000).toISOString(), rent_duration_hours: 6,
    row: 0, col: 10, row_span: 1, col_span: 1, owner_id: tiktokOwner,
  };

  const ytOwner = generateId();
  const ytRoot = {
    id: generateId(), user_handle: "@yt_creator", social_type: "youtube",
    media_url: "",
    live_link: "https://www.youtube.com/watch?v=jfKfPfyJRdk", color: "#FF0000",
    rent_start: new Date(now - 7200000).toISOString(), rent_duration_hours: 24,
    row: 1, col: 20, row_span: 4, col_span: 4, owner_id: ytOwner,
  };
  blocks["1-20"] = ytRoot;
  for (let r = 1; r <= 4; r++) {
    for (let c = 20; c <= 23; c++) {
      const key = `${r}-${c}`;
      if (key !== "1-20") blocks[key] = { owner_id: ytOwner, ref: "1-20" };
    }
  }

  const igOwner = generateId();
  const igRoot = {
    id: generateId(), user_handle: "@insta_live", social_type: "instagram",
    media_url: "https://picsum.photos/seed/insta1/200/200",
    live_link: "https://instagram.com", color: "#E1306C",
    rent_start: new Date(now - 900000).toISOString(), rent_duration_hours: 3,
    row: 8, col: 8, row_span: 2, col_span: 2, owner_id: igOwner,
  };
  blocks["8-8"] = igRoot;
  blocks["8-9"] = { owner_id: igOwner, ref: "8-8" };
  blocks["9-8"] = { owner_id: igOwner, ref: "8-8" };
  blocks["9-9"] = { owner_id: igOwner, ref: "8-8" };

  return blocks;
}

function initGrid() {
  const grid = {};
  for (let r = 0; r < GRID_ROWS; r++) {
    for (let c = 0; c < GRID_COLS; c++) grid[`${r}-${c}`] = null;
  }
  return grid;
}

/* ── Mappa righe DB → struttura grid in memoria ─────────── */
function rowsToGrid(rows) {
  const grid = initGrid();
  if (!rows || rows.length === 0) return grid;
  // Prima passata: piazza tutte le celle
  rows.forEach((r) => {
    const key = `${r.row}-${r.col}`;
    if (r.ref) {
      grid[key] = { owner_id: r.owner_id, ref: r.ref };
    } else {
      grid[key] = r;
    }
  });
  // Seconda passata: auto-merge di pixel adiacenti con STESSO contenuto
  // (utente che ha comprato 4 pixel singoli con stesso handle/link/social → 2x2 unito)
  return autoMergeAdjacent(grid);
}

function sameContent(a, b) {
  if (!a || !b || a.ref || b.ref) return false;
  return (
    a.user_handle === b.user_handle &&
    a.live_link === b.live_link &&
    a.social_type === b.social_type
  );
}

function autoMergeAdjacent(grid) {
  const rootKeys = Object.keys(grid).filter((k) => grid[k] && !grid[k].ref);
  const visited = new Set();

  for (const startKey of rootKeys) {
    if (visited.has(startKey)) continue;
    const startCell = grid[startKey];
    if (!startCell || startCell.ref) continue;

    // BFS sulle celle adiacenti con stesso contenuto
    const groupCells = [];
    const queue = [startKey];
    while (queue.length) {
      const k = queue.shift();
      if (visited.has(k)) continue;
      const cell = grid[k];
      if (!sameContent(cell, startCell)) continue;
      visited.add(k);
      groupCells.push(k);
      const [r, c] = k.split("-").map(Number);
      [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]].forEach(([nr, nc]) => {
        if (nr >= 0 && nr < GRID_ROWS && nc >= 0 && nc < GRID_COLS) {
          queue.push(`${nr}-${nc}`);
        }
      });
    }

    if (groupCells.length <= 1) continue;

    // bbox del gruppo
    const rs = groupCells.map((k) => parseInt(k.split("-")[0], 10));
    const cs = groupCells.map((k) => parseInt(k.split("-")[1], 10));
    const minR = Math.min(...rs), maxR = Math.max(...rs);
    const minC = Math.min(...cs), maxC = Math.max(...cs);
    const bboxArea = (maxR - minR + 1) * (maxC - minC + 1);

    // Merge solo se forma rettangolare perfetta (no L-shape o buchi)
    if (groupCells.length !== bboxArea) continue;

    const newRootKey = `${minR}-${minC}`;
    const ownerOfRoot = grid[newRootKey]?.owner_id || startCell.owner_id;
    // Promuovi/unifica la root al min(row,col)
    const newRoot = {
      ...startCell,
      ...(grid[newRootKey] || {}),
      row: minR,
      col: minC,
      row_span: maxR - minR + 1,
      col_span: maxC - minC + 1,
      owner_id: ownerOfRoot,
    };
    grid[newRootKey] = newRoot;
    // Tutte le altre celle del gruppo diventano refs della nuova root
    groupCells.forEach((k) => {
      if (k === newRootKey) return;
      grid[k] = { owner_id: ownerOfRoot, ref: newRootKey };
    });
  }
  return grid;
}

/* ============================================================
   COMPONENTE: singolo pixel occupato
   - Mostra thumbnail statica a riposo
   - Su hover: monta l'iframe YouTube con autoplay+mute (lazy)
   - Nessun window.open / nessun link esterno cliccabile
   ============================================================ */
function OccupiedPixel({ data, row, col, premium, autoOpenKey, onAutoOpenConsumed, grid, onExtend }) {
  const cellSize = useContext(CellSizeContext);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showExtend, setShowExtend] = useState(false);
  const tappedRef = useRef(false);
  const wrapperRef = useRef(null);
  const popoutRef = useRef(null);
  const [popoutPos, setPopoutPos] = useState(null);
  const social = SOCIALS[data.social_type] || SOCIALS.link;
  const rs = data.row_span || 1;
  const cs = data.col_span || 1;
  const w = cs * cellSize + (cs - 1);
  const h = rs * cellSize + (rs - 1);
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft(data.rent_start, data.rent_duration_hours));

  useEffect(() => {
    const t = setInterval(() => setTimeLeft(getTimeLeft(data.rent_start, data.rent_duration_hours)), 1000);
    return () => clearInterval(t);
  }, [data.rent_start, data.rent_duration_hours]);

  const embedInfo = getMediaEmbed(data.live_link, data.social_type);
  const hasEmbed = embedInfo.kind === "iframe" && !!embedInfo.iframeSrc;
  const thumbSrc = embedInfo.thumbnail || data.media_url || null;

  // Calcola posizione popout in coordinate viewport (portal → fuori dal transform di zoom)
  function computePopoutPos() {
    if (!wrapperRef.current) return null;
    const r = wrapperRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const PW = Math.min(360, vw - 16);
    // +28 banner LIVE + 40 share/extend row + 70 extra se menu estendi aperto
    const extendExtra = showExtend ? 76 : 0;
    const PH = (hasEmbed ? Math.round(PW * 9 / 16) + 28 + 40 : 130 + 40) + extendExtra;
    let left = r.left + r.width / 2 - PW / 2;
    let top = r.top - PH - 10;
    if (top < 8) top = r.bottom + 10;
    if (top + PH > vh - 8) top = Math.max(8, vh - PH - 8);
    left = Math.max(8, Math.min(vw - PW - 8, left));
    return { left, top, width: PW, height: PH };
  }

  function openPopout() {
    setOpen(true);
    setPopoutPos(computePopoutPos());
  }
  function closePopout() {
    setOpen(false);
    setShowExtend(false);
    setPopoutPos(null);
  }

  function handleEnter() {
    if (tappedRef.current) return;
    openPopout();
  }
  function handleLeave() {
    if (tappedRef.current) return;
    closePopout();
  }
  // Tap/click: toggle popout (mobile-first), evita interferenze col pan
  function handleClick(e) {
    e.stopPropagation();
    if (open && tappedRef.current) {
      tappedRef.current = false;
      closePopout();
    } else {
      tappedRef.current = true;
      openPopout();
    }
  }

  // Chiudi su click fuori (sia mouse che touch)
  useEffect(() => {
    if (!open || !tappedRef.current) return;
    function onDocPointerDown(e) {
      const inPixel = wrapperRef.current?.contains(e.target);
      const inPopout = popoutRef.current?.contains(e.target);
      if (!inPixel && !inPopout) {
        tappedRef.current = false;
        closePopout();
      }
    }
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("touchstart", onDocPointerDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("touchstart", onDocPointerDown);
    };
  }, [open]);

  // Riposiziona popout su resize/scroll o quando il menu estendi cambia
  useEffect(() => {
    if (!open) return;
    setPopoutPos(computePopoutPos());
    function reposition() { setPopoutPos(computePopoutPos()); }
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, showExtend]);

  // Auto-open via deep link ?p=row,col
  useEffect(() => {
    if (!autoOpenKey) return;
    if (autoOpenKey !== `${row}-${col}`) return;
    // attendiamo che il zoomToElement completi (~700ms) poi apriamo
    const t = setTimeout(() => {
      tappedRef.current = true;
      openPopout();
      if (onAutoOpenConsumed) onAutoOpenConsumed();
    }, 750);
    return () => clearTimeout(t);
  }, [autoOpenKey, row, col]);

  // Condividi link al pixel
  function handleShare(e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("p", `${row},${col}`);
      const link = url.toString();
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(link);
      } else {
        // fallback
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch { /* noop */ }
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  }

  // ─── Estensione blocco: calcola direzioni disponibili e prezzo ───
  const rs2 = data.row_span || 1, cs2 = data.col_span || 1;
  function dirAvailable(dir) {
    if (!grid) return null;
    const cells = [];
    if (dir === "up") {
      if (row <= 0) return null;
      for (let cc = col; cc < col + cs2; cc++) cells.push([row - 1, cc]);
    } else if (dir === "down") {
      if (row + rs2 >= GRID_ROWS) return null;
      for (let cc = col; cc < col + cs2; cc++) cells.push([row + rs2, cc]);
    } else if (dir === "left") {
      if (col <= 0) return null;
      for (let rr = row; rr < row + rs2; rr++) cells.push([rr, col - 1]);
    } else if (dir === "right") {
      if (col + cs2 >= GRID_COLS) return null;
      for (let rr = row; rr < row + rs2; rr++) cells.push([rr, col + cs2]);
    }
    if (cells.some(([r, c]) => grid[`${r}-${c}`])) return null;
    // Prezzo: tier derivato dalle ore di affitto del blocco originale
    const h = data.rent_duration_hours;
    const tierKey = h === 720 ? "month" : h === 168 ? "week" : h === 24 ? "day" : "hour";
    let price = 0;
    cells.forEach(([r, c]) => {
      const isPrm = (r >= PREMIUM_START_ROW && r < PREMIUM_END_ROW && c >= PREMIUM_START_COL && c < PREMIUM_END_COL);
      price += (isPrm ? PRICING.premium : PRICING.standard)[tierKey];
    });
    return { cells, price, tierKey };
  }
  const extendOptions = open && showExtend
    ? ["up", "right", "down", "left"].map((d) => ({ dir: d, info: dirAvailable(d) }))
    : [];

  function handleExtendClick(dir) {
    if (!onExtend) return;
    onExtend(data, dir);
    setShowExtend(false);
  }

  return (
    <div
      ref={wrapperRef}
      data-testid={`occupied-pixel-${row}-${col}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={handleClick}
      style={{
        gridRow: `${row + 1} / span ${rs}`,
        gridColumn: `${col + 1} / span ${cs}`,
        width: w,
        height: h,
        maxWidth: w,
        maxHeight: h,
        minWidth: 0,
        minHeight: 0,
        boxSizing: "border-box",
        contain: "layout size paint",
        position: "relative",
        overflow: "hidden",
        borderRadius: rs > 1 || cs > 1 ? 4 : 2,
        border: premium ? "1px solid #ffd700" : "1px solid #1e3a5f",
        cursor: "zoom-in",
      }}
      title={`${data.user_handle} · ${social.label} · tap/hover per anteprima`}
    >
      {/* Thumbnail / placeholder */}
      {thumbSrc ? (
        <img
          src={thumbSrc}
          alt=""
          loading="lazy"
          decoding="async"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            maxWidth: "100%", maxHeight: "100%",
            objectFit: "cover", display: "block",
          }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      ) : (
        <div style={{
          position: "absolute", inset: 0,
          background: `${data.color}33`,
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "hidden",
        }}>
          {embedInfo.favicon ? (
            <img src={embedInfo.favicon} alt="" loading="lazy" style={{ width: "60%", height: "60%", maxWidth: "60%", maxHeight: "60%", objectFit: "contain", opacity: 0.8 }} onError={(e) => { e.target.style.display = "none"; }} />
          ) : null}
        </div>
      )}

      {/* Tint */}
      <div style={{ position: "absolute", inset: 0, background: `${data.color}22`, pointerEvents: "none" }} />

      {/* Glow indicator popout aperto */}
      {open && (
        <div style={{
          position: "absolute", inset: 0,
          boxShadow: "0 0 0 2px #5eead4, 0 0 14px rgba(94,234,212,0.6)",
          pointerEvents: "none",
        }} />
      )}

      {/* HUD */}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 5 }}>
        <div style={{
          position: "absolute", top: 2, left: 2, width: 16, height: 16, borderRadius: 4,
          background: social.gradient || social.color,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {social.svg}
        </div>

        {(rs > 1 || cs > 1) && (
          <>
            <div style={{
              position: "absolute", top: 2, left: 20, background: "#ff2d2d", borderRadius: 3,
              padding: "1px 4px", fontSize: 7, color: "#fff", fontWeight: 700,
            }}>
              ● LIVE
            </div>
            <div style={{
              position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.72)", borderRadius: 3,
              padding: "1px 4px", fontSize: 8, fontFamily: "monospace",
              color: timeLeft < 1800000 ? "#ef4444" : "#94a3b8",
            }}>
              {formatTime(timeLeft)}
            </div>
          </>
        )}

        {h >= 26 && (
          <div style={{
            position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.72)",
            padding: "1px 4px", fontSize: Math.max(7, Math.min(10, h / 5)), color: "#fff",
            fontWeight: 600, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
          }}>
            {data.user_handle}
          </div>
        )}

        {premium && (
          <div className="glow-premium" style={{ position: "absolute", inset: 0 }} />
        )}
      </div>

      {/* Popout (portal → fuori da transform parent) */}
      {open && popoutPos && createPortal(
        <div
          ref={popoutRef}
          data-testid={`popout-${row}-${col}`}
          style={{
            position: "fixed",
            left: popoutPos.left,
            top: popoutPos.top,
            width: popoutPos.width,
            height: popoutPos.height,
            background: "#000",
            border: "2px solid #5eead4",
            borderRadius: 12,
            boxShadow: "0 12px 32px rgba(0,0,0,0.6), 0 0 24px rgba(94,234,212,0.25)",
            overflow: "hidden",
            zIndex: 9999,
            pointerEvents: tappedRef.current ? "auto" : "none",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {hasEmbed ? (
            <>
              <iframe
                data-testid={`embed-iframe-${row}-${col}`}
                src={embedInfo.iframeSrc}
                title={data.user_handle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: `calc(100% - 68px)`, border: "none", display: "block", background: "#000" }}
              />
              <div style={{
                position: "absolute", bottom: 40, left: 0, right: 0, height: 28,
                background: "rgba(0,0,0,0.88)", color: "#e2e8f0",
                display: "flex", alignItems: "center", padding: "0 10px",
                fontSize: 11, fontWeight: 600, gap: 8,
              }}>
                <span style={{ color: "#ff2d2d" }}>● LIVE</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{data.user_handle}</span>
                <span style={{ marginLeft: "auto", fontFamily: "monospace", color: "#94a3b8" }}>{formatTime(timeLeft)}</span>
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: 6, background: "#000", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                {showExtend && (
                  <div style={{ marginBottom: 6, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                    {extendOptions.map(({ dir, info }) => {
                      const labels = { up: "↑ Sopra", down: "↓ Sotto", left: "← Sinistra", right: "→ Destra" };
                      const disabled = !info;
                      return (
                        <button
                          key={dir}
                          data-testid={`extend-${dir}-${row}-${col}`}
                          disabled={disabled}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (info) handleExtendClick(dir); }}
                          style={{
                            background: disabled ? "rgba(30,58,95,0.4)" : "rgba(255,215,0,0.12)",
                            color: disabled ? "#475569" : "#ffd700",
                            border: `1px solid ${disabled ? "#1e3a5f" : "rgba(255,215,0,0.45)"}`,
                            borderRadius: 5, padding: "5px 6px",
                            fontSize: 10, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
                            textAlign: "left", lineHeight: 1.2,
                          }}
                        >
                          <div>{labels[dir]}</div>
                          <div style={{ fontSize: 9, opacity: 0.85, marginTop: 1 }}>
                            {info ? `+${info.cells.length}px · €${info.price.toFixed(2)}` : "n/d"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    data-testid={`share-btn-${row}-${col}`}
                    onClick={handleShare}
                    className={`popout-share${copied ? " copied" : ""}`}
                    style={{ flex: 1 }}
                  >
                    {copied ? "✓ LINK COPIATO" : "↗ CONDIVIDI"}
                  </button>
                  <button
                    data-testid={`extend-btn-${row}-${col}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowExtend((v) => !v); }}
                    className="popout-extend"
                    style={{ flex: 1 }}
                    title="Estendi il blocco con pixel adiacenti (pagamento Stripe)"
                  >
                    {showExtend ? "⊖ Chiudi" : "⊕ Estendi"}
                  </button>
                </div>
              </div>
            </>
          ) : (
            // ─── Card fallback: favicon + host + handle + CTA ───
            <>
              <a
                href={data.live_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: "flex", flexDirection: "column",
                  position: "absolute", top: 0, left: 0, right: 0, bottom: 40,
                  textDecoration: "none", color: "#e2e8f0",
                  background: embedInfo.brandGradient || `linear-gradient(135deg, ${data.color}22 0%, #000 70%)`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, flex: 1 }}>
                  <div style={{
                    width: 54, height: 54, borderRadius: 12,
                    background: embedInfo.isTwitch ? "#fff" : (social.gradient || social.color),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "2px solid rgba(255,255,255,0.18)", flexShrink: 0,
                  }}>
                    {embedInfo.isTwitch ? (
                      <svg viewBox="0 0 24 24" fill="#9146FF" width="32" height="32">
                        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
                      </svg>
                    ) : embedInfo.favicon ? (
                      <img src={embedInfo.favicon} alt="" style={{ width: 32, height: 32, objectFit: "contain" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <div style={{ transform: "scale(2.5)" }}>{social.svg}</div>
                    )}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: embedInfo.isTwitch ? "rgba(255,255,255,0.85)" : "#94a3b8", textTransform: "uppercase" }}>
                      {embedInfo.providerLabel}{embedInfo.channel ? ` · ${embedInfo.channel}` : ""}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {data.user_handle}
                    </div>
                    <div style={{ fontSize: 11, color: embedInfo.isTwitch ? "rgba(255,255,255,0.7)" : "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>
                      {embedInfo.host}
                    </div>
                  </div>
                </div>
                <div style={{
                  height: 28, background: "rgba(0,0,0,0.88)",
                  display: "flex", alignItems: "center", padding: "0 10px",
                  fontSize: 11, fontWeight: 700, gap: 8,
                  borderTop: "1px solid rgba(255,255,255,0.08)",
                  letterSpacing: 0.5,
                }}>
                  <span style={{ color: embedInfo.isTwitch ? "#c89bff" : "#5eead4" }}>↗ {embedInfo.ctaText || "APRI LINK"}</span>
                  <span style={{ marginLeft: "auto", fontFamily: "monospace", color: "#94a3b8", fontWeight: 600 }}>{formatTime(timeLeft)}</span>
                </div>
              </a>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: 6, background: "#000", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                {showExtend && (
                  <div style={{ marginBottom: 6, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                    {extendOptions.map(({ dir, info }) => {
                      const labels = { up: "↑ Sopra", down: "↓ Sotto", left: "← Sinistra", right: "→ Destra" };
                      const disabled = !info;
                      return (
                        <button
                          key={dir}
                          data-testid={`extend-${dir}-${row}-${col}`}
                          disabled={disabled}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (info) handleExtendClick(dir); }}
                          style={{
                            background: disabled ? "rgba(30,58,95,0.4)" : "rgba(255,215,0,0.12)",
                            color: disabled ? "#475569" : "#ffd700",
                            border: `1px solid ${disabled ? "#1e3a5f" : "rgba(255,215,0,0.45)"}`,
                            borderRadius: 5, padding: "5px 6px",
                            fontSize: 10, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
                            textAlign: "left", lineHeight: 1.2,
                          }}
                        >
                          <div>{labels[dir]}</div>
                          <div style={{ fontSize: 9, opacity: 0.85, marginTop: 1 }}>
                            {info ? `+${info.cells.length}px · €${info.price.toFixed(2)}` : "n/d"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    data-testid={`share-btn-${row}-${col}`}
                    onClick={handleShare}
                    className={`popout-share${copied ? " copied" : ""}`}
                    style={{ flex: 1 }}
                  >
                    {copied ? "✓ LINK COPIATO" : "↗ CONDIVIDI"}
                  </button>
                  <button
                    data-testid={`extend-btn-${row}-${col}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowExtend((v) => !v); }}
                    className="popout-extend"
                    style={{ flex: 1 }}
                    title="Estendi il blocco con pixel adiacenti (pagamento Stripe)"
                  >
                    {showExtend ? "⊖ Chiudi" : "⊕ Estendi"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ============================================================
   Zoom controls overlay (rotella, pinch, +, -, reset)
   ============================================================ */
function ZoomControls() {
  const { zoomIn, zoomOut, resetTransform, centerView } = useControls();
  const btn = {
    width: 34, height: 34, borderRadius: 8,
    background: "rgba(15,21,37,0.92)", border: "1px solid #1e3a5f",
    color: "#e2e8f0", cursor: "pointer", fontSize: 16, fontWeight: 700,
    display: "flex", alignItems: "center", justifyContent: "center",
    backdropFilter: "blur(6px)",
  };
  return (
    <div style={{
      position: "absolute", right: 12, bottom: 12, zIndex: 50,
      display: "flex", flexDirection: "column", gap: 6,
    }}>
      <button data-testid="zoom-in-btn" style={btn} onClick={() => zoomIn(0.4)} title="Zoom +">+</button>
      <button data-testid="zoom-out-btn" style={btn} onClick={() => zoomOut(0.4)} title="Zoom -">−</button>
      <button data-testid="zoom-reset-btn" style={{ ...btn, fontSize: 11 }} onClick={() => { resetTransform(); centerView(); }} title="Reset zoom">⟲</button>
    </div>
  );
}

/* ============================================================
   Online users indicator (finto ma realistico)
   ============================================================ */
function OnlineIndicator() {
  const [count, setCount] = useState(() => 119 + Math.floor(Math.random() * (349 - 119 + 1)));
  useEffect(() => {
    let cancelled = false;
    let to;
    function schedule() {
      const delay = 4000 + Math.floor(Math.random() * 3000); // 4–7s
      to = setTimeout(() => {
        if (cancelled) return;
        setCount((c) => {
          const choices = [-3, -2, -1, 1, 2, 3];
          const delta = choices[Math.floor(Math.random() * choices.length)];
          let next = c + delta;
          if (next < 119) next = 119 + Math.abs(delta);
          if (next > 349) next = 349 - Math.abs(delta);
          return next;
        });
        schedule();
      }, delay);
    }
    schedule();
    return () => { cancelled = true; clearTimeout(to); };
  }, []);
  return (
    <div data-testid="online-indicator" className="online-indicator">
      <span className="online-led" />
      <span className="online-label">Online: </span>
      <b className="online-count" data-testid="online-count">{count}</b>
    </div>
  );
}

/* ============================================================
   Auto-focus su pixel target (deep link ?p=row,col)
   ============================================================ */
function AutoFocusToPixel({ targetKey, onDone }) {
  const { zoomToElement } = useControls();
  useEffect(() => {
    if (!targetKey) return;
    const t = setTimeout(() => {
      const node = document.querySelector(`[data-testid="occupied-pixel-${targetKey}"]`);
      if (node) {
        try { zoomToElement(node, 2.5, 700); } catch { /* noop */ }
      }
      if (onDone) onDone();
    }, 400);
    return () => clearTimeout(t);
  }, [targetKey, zoomToElement, onDone]);
  return null;
}

/* AutoFitGrid: scala e centra la griglia una SOLA VOLTA al primo mount.
   Non si riattiva su re-render (evita reset dello zoom durante pinch/pan utente
   o tick del countdown che re-rendonizza il tree). Fa skip se il deep-link
   ?p=r,c sta per attivare AutoFocusToPixel. */
function AutoFitGrid({ autoOpenKey }) {
  const { setTransform } = useControls();
  const didFitRef = useRef(false);
  const setTransformRef = useRef(setTransform);
  setTransformRef.current = setTransform;
  useEffect(() => {
    if (autoOpenKey) return; // deep-link mode: AutoFocusToPixel gestisce il pan/zoom
    if (didFitRef.current) return;
    const t = setTimeout(() => {
      const stage = document.querySelector('[data-testid="zoom-stage"]');
      const grid = document.querySelector('[data-testid="pixel-grid"]');
      if (!stage || !grid) return;
      const stageW = stage.clientWidth;
      const stageH = stage.clientHeight;
      const gridW = grid.offsetWidth;
      const gridH = grid.offsetHeight;
      if (!stageW || !gridW) return;
      const scaleX = (stageW - 20) / gridW;
      const scaleY = (stageH - 20) / gridH;
      const fit = Math.min(1, scaleX, scaleY);
      const scale = Math.max(0.4, fit);
      const contentW = gridW * scale;
      const contentH = gridH * scale;
      const outerPadding = 24 * scale;
      const posX = (stageW - contentW) / 2 - outerPadding;
      const posY = (stageH - contentH) / 2 - outerPadding;
      try { setTransformRef.current(posX, posY, scale, 0); } catch { /* noop */ }
      didFitRef.current = true;
    }, 350);
    return () => clearTimeout(t);
  }, []); // one-shot: NON dipende da cellSize/setTransform/autoOpenKey per evitare reset
  return null;
}

/* ============================================================
   Sparkle burst (effetto fuochi d'artificio dorati su acquisto Premium)
   ============================================================ */
function SparkleBurst({ x, y }) {
  const PARTICLES = 14;
  return (
    <div
      data-testid="sparkle-burst"
      style={{
        position: "absolute",
        left: x, top: y,
        width: 0, height: 0,
        pointerEvents: "none",
        zIndex: 50,
      }}
    >
      {/* Flash centrale */}
      <div className="sparkle-core" />
      {/* Anello che si espande */}
      <div className="sparkle-ring" />
      {/* Particelle */}
      {Array.from({ length: PARTICLES }).map((_, i) => {
        const angle = (i / PARTICLES) * Math.PI * 2;
        const dist = 60 + Math.random() * 40;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const size = 4 + Math.random() * 4;
        const delay = Math.random() * 0.12;
        return (
          <span
            key={i}
            className="sparkle-particle"
            style={{
              width: size, height: size,
              ["--dx"]: `${dx}px`,
              ["--dy"]: `${dy}px`,
              animationDelay: `${delay}s`,
            }}
          />
        );
      })}
    </div>
  );
}

/* ============================================================
   COMPONENTE PRINCIPALE
   ============================================================ */
export default function PixelBoard() {
  const useSupabase = isSupabaseConfigured();

  const [autoOpenKey, setAutoOpenKey] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const p = new URL(window.location.href).searchParams.get("p");
      if (!p) return null;
      const [r, c] = p.split(",").map((x) => parseInt(x, 10));
      if (Number.isFinite(r) && Number.isFinite(c) && r >= 0 && r < GRID_ROWS && c >= 0 && c < GRID_COLS) {
        return `${r}-${c}`;
      }
    } catch { /* noop */ }
    return null;
  });

  const [grid, setGrid] = useState(() => {
    if (useSupabase) return initGrid();
    const g = initGrid();
    Object.entries(getDemoPixels()).forEach(([k, v]) => { g[k] = v; });
    return g;
  });

  // Se autoOpenKey punta a un ref (pixel non-root), redirigi sulla root del blocco
  useEffect(() => {
    if (!autoOpenKey) return;
    const cell = grid[autoOpenKey];
    if (cell && cell.ref && cell.ref !== autoOpenKey) {
      setAutoOpenKey(cell.ref);
    }
  }, [autoOpenKey, grid]);
  const [selected, setSelected] = useState([]);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [panel, setPanel] = useState(null);
  const [form, setForm] = useState({
    social_type: "tiktok", user_handle: "", live_link: "", media_url: "", rent_tier: "hour",
  });
  const [buying, setBuying] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(useSupabase);
  const [cellSize, setCellSize] = useState(DEFAULT_CELL);
  const [sparkles, setSparkles] = useState([]); // array di animazioni in corso
  const stageRef = useRef(null);

  // Calcolo cellSize fluidamente in base alla larghezza dello stage
  useEffect(() => {
    if (!stageRef.current) return;
    function recalc() {
      if (!stageRef.current) return;
      const w = stageRef.current.clientWidth;
      // riserva ~ 48px di padding interno + 24px per i controlli zoom
      const avail = Math.max(0, w - 56);
      const gapCount = GRID_COLS - 1; // 1px per gap
      let cs = Math.floor((avail - gapCount) / GRID_COLS);
      if (cs < MIN_CELL) cs = MIN_CELL;
      if (cs > MAX_CELL) cs = MAX_CELL;
      setCellSize(cs);
    }
    recalc();
    const ro = new ResizeObserver(recalc);
    ro.observe(stageRef.current);
    window.addEventListener("orientationchange", recalc);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", recalc);
    };
  }, []);

  // Cleanup sparkles dopo 1.6s
  useEffect(() => {
    if (sparkles.length === 0) return;
    const t = setTimeout(() => {
      const now = Date.now();
      setSparkles((arr) => arr.filter((s) => now - s.time < 1600));
    }, 1600);
    return () => clearTimeout(t);
  }, [sparkles]);

  /* SUPABASE: initial load + realtime subscription */
  useEffect(() => {
    if (!useSupabase) return;

    let mounted = true;

    async function loadPixels() {
      try {
        const { data, error } = await supabase.from("pixels").select("*").limit(GRID_ROWS * GRID_COLS);
        if (!mounted) return;
        if (error) {
          console.error("Supabase load error:", error);
          setErrorMsg(`Supabase: ${error.message}. Modalità demo attiva.`);
          setGrid(() => {
            const g = initGrid();
            Object.entries(getDemoPixels()).forEach(([k, v]) => { g[k] = v; });
            return g;
          });
          setLoading(false);
          return;
        }
        setGrid(rowsToGrid(data || []));
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        console.error("Supabase network error:", err);
        setErrorMsg("Impossibile contattare Supabase (URL/chiavi?). Modalità demo attiva.");
        setGrid(() => {
          const g = initGrid();
          Object.entries(getDemoPixels()).forEach(([k, v]) => { g[k] = v; });
          return g;
        });
        setLoading(false);
      }
    }

    loadPixels();

    const channel = supabase
      .channel("pixels-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "pixels" },
        () => {
          loadPixels();
        }
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [useSupabase]);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Scadenza automatica dei pixel: mascheriamo solo lato UI.
  // La cancellazione effettiva avviene su backend via cron (service_role) — la anon key
  // non ha più i permessi DELETE/UPDATE/INSERT su Supabase.
  useEffect(() => {
    setGrid((prev) => {
      const toRemove = new Set();
      Object.values(prev).forEach((val) => {
        if (val && !val.ref && getTimeLeft(val.rent_start, val.rent_duration_hours) === 0) {
          toRemove.add(val.owner_id);
        }
      });
      if (toRemove.size === 0) return prev;
      const next = { ...prev };
      Object.entries(next).forEach(([key, val]) => {
        if (!val) return;
        const ownerId = val.owner_id || (val.ref && next[val.ref]?.owner_id);
        if (toRemove.has(ownerId)) next[key] = null;
      });
      return next;
    });
  }, [tick]);

  function handleEmptyClick(row, col) {
    // Se è in corso (o appena terminato) un drag-pan reale, ignora il click
    if (panningRef.current) return;
    const key = `${row}-${col}`;
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }

  // Rileviamo a mano un drag reale dell'utente (mousedown→mousemove>5px→mouseup)
  // così da NON bloccare i click dopo reset/zoom programmatici della libreria.
  const panningRef = useRef(false);
  const dragStartRef = useRef(null);
  function onStageMouseDown(e) {
    if (e.button !== 0) return;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    panningRef.current = false;
  }
  function onStageMouseMove(e) {
    const s = dragStartRef.current;
    if (!s) return;
    if (Math.abs(e.clientX - s.x) > 5 || Math.abs(e.clientY - s.y) > 5) {
      panningRef.current = true;
    }
  }
  function onStageMouseUp() {
    dragStartRef.current = null;
    if (panningRef.current) {
      // tieni il flag per assorbire l'eventuale click sintetico
      setTimeout(() => { panningRef.current = false; }, 250);
    }
  }

  function openBuyPanel() {
    if (selected.length > 0) setPanel("buy");
  }

  // Bounding box rettangolare di tutte le celle selezionate
  // (la bacheca FONDE i pixel in un unico blocco rettangolare)
  const bboxInfo = (() => {
    if (selected.length === 0) {
      return { keys: [], minRow: 0, minCol: 0, rs: 0, cs: 0, premiumCount: 0, standardCount: 0, conflicts: [] };
    }
    const rows = selected.map((k) => parseInt(k.split("-")[0], 10));
    const cols = selected.map((k) => parseInt(k.split("-")[1], 10));
    const minRow = Math.min(...rows), maxRow = Math.max(...rows);
    const minCol = Math.min(...cols), maxCol = Math.max(...cols);
    const keys = [];
    const conflicts = [];
    let premiumCount = 0;
    for (let r = minRow; r <= maxRow; r++) {
      for (let c = minCol; c <= maxCol; c++) {
        const k = `${r}-${c}`;
        keys.push(k);
        if (isPremium(r, c)) premiumCount++;
        if (grid[k]) conflicts.push(k);
      }
    }
    return {
      keys, minRow, minCol,
      rs: maxRow - minRow + 1, cs: maxCol - minCol + 1,
      premiumCount, standardCount: keys.length - premiumCount,
      conflicts,
    };
  })();

  // Pricing tier helper
  const tierPriceStandard = PRICING.standard[form.rent_tier] || 0;
  const tierPricePremium  = PRICING.premium[form.rent_tier]  || 0;
  // Conteggio pixel premium DISPONIBILI (zona 6×6 centrale)
  const premiumAvailable = (() => {
    let n = 0;
    for (let r = PREMIUM_START_ROW; r < PREMIUM_END_ROW; r++) {
      for (let c = PREMIUM_START_COL; c < PREMIUM_END_COL; c++) {
        if (!grid[`${r}-${c}`]) n++;
      }
    }
    return n;
  })();
  const totalPriceNum = bboxInfo.standardCount * tierPriceStandard + bboxInfo.premiumCount * tierPricePremium;
  const totalPrice = totalPriceNum.toFixed(2);
  const tierMeta = DURATIONS.find((d) => d.key === form.rent_tier) || DURATIONS[0];
  // Backward-compat per renderer esistenti
  const selectedPremium = bboxInfo.premiumCount;
  const selectedStandard = bboxInfo.standardCount;

  // Prezzo orario "minimo" per la selection-bar (tier "hour")
  const pricePerHour = bboxInfo.standardCount * PRICING.standard.hour + bboxInfo.premiumCount * PRICING.premium.hour;

  // Estensione di un blocco esistente: ridiretta a Stripe Checkout (paga solo le celle nuove)
  async function handleExtendBlock(rootData, direction) {
    const r = rootData.row, c = rootData.col;
    const rs = rootData.row_span || 1, cs = rootData.col_span || 1;
    let newR = r, newC = c, newRs = rs, newCs = cs;
    const newCells = [];
    if (direction === "up") {
      if (r <= 0) return;
      newR = r - 1; newRs = rs + 1;
      for (let cc = c; cc < c + cs; cc++) newCells.push([r - 1, cc]);
    } else if (direction === "down") {
      if (r + rs >= GRID_ROWS) return;
      newRs = rs + 1;
      for (let cc = c; cc < c + cs; cc++) newCells.push([r + rs, cc]);
    } else if (direction === "left") {
      if (c <= 0) return;
      newC = c - 1; newCs = cs + 1;
      for (let rr = r; rr < r + rs; rr++) newCells.push([rr, c - 1]);
    } else if (direction === "right") {
      if (c + cs >= GRID_COLS) return;
      newCs = cs + 1;
      for (let rr = r; rr < r + rs; rr++) newCells.push([rr, c + cs]);
    }
    for (const [rr, cc] of newCells) {
      if (grid[`${rr}-${cc}`]) {
        setErrorMsg("Lo spazio adiacente non è più libero.");
        setTimeout(() => setErrorMsg(""), 3500);
        return;
      }
    }
    // Tier derivato dalla durata del blocco originale
    const h = rootData.rent_duration_hours;
    const tierKey = h === 720 ? "month" : h === 168 ? "week" : h === 24 ? "day" : "hour";

    // iOS Safari / mobile: pre-open synchronous tab per evitare blocco redirect post-await.
    const isMobile = typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
    let preopened = null;
    if (isMobile) {
      try { preopened = window.open("about:blank", "_blank"); } catch { /* noop */ }
    }

    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const payload = {
        pixel_data: {
          min_row: newR, min_col: newC, rs: newRs, cs: newCs,
          user_handle: rootData.user_handle,
          live_link: rootData.live_link,
          social_type: rootData.social_type,
          media_url: rootData.media_url || null,
          color: rootData.color || "#333",
          tier: tierKey,
          extend_owner_id: rootData.owner_id,
          extend_old_min_row: r,
          extend_old_min_col: c,
          extend_old_rs: rs,
          extend_old_cs: cs,
        },
        origin_url: window.location.origin,
      };
      const res = await fetch(`${apiBase}/api/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        if (preopened) { try { preopened.close(); } catch { /* noop */ } }
        const txt = await res.text();
        let msg = txt;
        try { msg = JSON.parse(txt).detail || txt; } catch { /* noop */ }
        setErrorMsg(msg);
        setTimeout(() => setErrorMsg(""), 4500);
        return;
      }
      const data = await res.json();
      try { localStorage.setItem("livegrid_pending_session", data.session_id); } catch { /* noop */ }
      if (preopened && !preopened.closed) {
        try {
          preopened.location.href = data.url;
        } catch {
          window.location.assign(data.url);
        }
      } else {
        window.location.assign(data.url);
      }
    } catch (e) {
      if (preopened) { try { preopened.close(); } catch { /* noop */ } }
      console.error("Extend checkout error:", e);
      setErrorMsg("Impossibile aprire il checkout. Verifica la connessione e riprova.");
      setTimeout(() => setErrorMsg(""), 4500);
    }
  }

  async function handleBuy() {
    if (!form.user_handle || !form.live_link || selected.length === 0) return;
    if (bboxInfo.conflicts.length > 0) {
      setErrorMsg(`Il blocco selezionato sovrappone pixel già occupati. Seleziona un'area libera.`);
      return;
    }
    setBuying(true);
    setErrorMsg("");

    // iOS Safari / mobile WebView: pre-open un tab SINCRONO dentro lo user-gesture
    // così il redirect post-await non viene bloccato dal popup blocker.
    // Su desktop o se il popup viene bloccato, ricadiamo sul redirect same-tab.
    const isMobile = typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
    let preopened = null;
    if (isMobile) {
      try { preopened = window.open("about:blank", "_blank"); } catch { /* noop */ }
    }

    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const payload = {
        pixel_data: {
          min_row: bboxInfo.minRow,
          min_col: bboxInfo.minCol,
          rs: bboxInfo.rs,
          cs: bboxInfo.cs,
          user_handle: form.user_handle,
          live_link: form.live_link,
          social_type: form.social_type,
          media_url: form.media_url || null,
          color: SOCIALS[form.social_type]?.color || "#333",
          tier: form.rent_tier,
        },
        origin_url: window.location.origin,
      };
      const res = await fetch(`${apiBase}/api/checkout/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        if (preopened) { try { preopened.close(); } catch { /* noop */ } }
        const txt = await res.text();
        let msg = txt;
        try { msg = JSON.parse(txt).detail || txt; } catch { /* noop */ }
        setErrorMsg(msg);
        setBuying(false);
        return;
      }
      const data = await res.json();
      // localStorage (non sessionStorage) per sopravvivere al cambio di tab su mobile
      try { localStorage.setItem("livegrid_pending_session", data.session_id); } catch { /* noop */ }
      if (bboxInfo.premiumCount > 0) {
        const centerX = ((bboxInfo.minCol + bboxInfo.cs / 2) * cellSize);
        const centerY = ((bboxInfo.minRow + bboxInfo.rs / 2) * cellSize);
        try { localStorage.setItem("livegrid_pending_sparkle", JSON.stringify({ x: centerX, y: centerY })); } catch { /* noop */ }
      }
      // Redirect: usa il tab pre-aperto su mobile, altrimenti same-tab
      if (preopened && !preopened.closed) {
        try {
          preopened.location.href = data.url;
        } catch {
          window.location.assign(data.url);
        }
      } else {
        window.location.assign(data.url);
      }
    } catch (e) {
      if (preopened) { try { preopened.close(); } catch { /* noop */ } }
      console.error("Stripe checkout error:", e);
      setErrorMsg("Impossibile aprire il checkout. Verifica la connessione e riprova.");
      setBuying(false);
    }
  }

  // ─── Gestione ritorno da Stripe Checkout: polling status ───
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const sid = url.searchParams.get("stripe_session");
    const cancelled = url.searchParams.get("stripe_cancel");
    if (cancelled) {
      setErrorMsg("Pagamento annullato. Riprova quando vuoi.");
      url.searchParams.delete("stripe_cancel");
      window.history.replaceState({}, "", url.toString());
      return;
    }
    if (!sid) return;
    let attempts = 0;
    const maxAttempts = 12;
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    setSuccessMsg("Verifica del pagamento in corso…");

    async function poll() {
      attempts++;
      try {
        const r = await fetch(`${apiBase}/api/checkout/status/${sid}`);
        const d = await r.json();
        if (d.payment_status === "paid" && d.pixels_persisted) {
          setSuccessMsg("✓ Pagamento ricevuto e pixel attivati!");
          setTimeout(() => setSuccessMsg(""), 4000);
          url.searchParams.delete("stripe_session");
          window.history.replaceState({}, "", url.toString());
          try { localStorage.removeItem("livegrid_pending_session"); } catch { /* noop */ }
          try { sessionStorage.removeItem("livegrid_pending_session"); } catch { /* noop */ }
          // Sparkle premium se eravamo in attesa
          try {
            const sp = localStorage.getItem("livegrid_pending_sparkle") || sessionStorage.getItem("livegrid_pending_sparkle");
            if (sp) {
              const { x, y } = JSON.parse(sp);
              setSparkles((arr) => [...arr, { id: generateId(), x, y, time: Date.now() }]);
              try { localStorage.removeItem("livegrid_pending_sparkle"); } catch { /* noop */ }
              try { sessionStorage.removeItem("livegrid_pending_sparkle"); } catch { /* noop */ }
            }
          } catch { /* noop */ }
          return;
        }
        if (d.status === "expired" || d.payment_status === "unpaid") {
          setErrorMsg("Sessione scaduta o pagamento non riuscito.");
          url.searchParams.delete("stripe_session");
          window.history.replaceState({}, "", url.toString());
          return;
        }
        // Pagato ma persistenza fallita: mostra l'errore concreto e smetti di pollare
        if (d.payment_status === "paid" && !d.pixels_persisted && d.error_message) {
          setSuccessMsg("");
          setErrorMsg(`Pagamento ricevuto ma errore nel salvataggio dei pixel: ${d.error_message}. Contatta il supporto con questo ID: ${sid}`);
          url.searchParams.delete("stripe_session");
          window.history.replaceState({}, "", url.toString());
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setSuccessMsg("");
          setErrorMsg(`Timeout verifica pagamento. ID sessione: ${sid}. Riapri la pagina tra 1 minuto.`);
        }
      } catch (e) {
        if (attempts < maxAttempts) setTimeout(poll, 2000);
      }
    }
    poll();
  }, []);

  const liveCount = Object.values(grid).filter((v) => v && !v.ref).length;
  const embedPreview = form.live_link ? getMediaEmbed(form.live_link, form.social_type) : null;

  return (
    <div style={{
      minHeight: "100vh", background: "#080b14", fontFamily: "'Inter', sans-serif",
      color: "#e2e8f0", display: "flex", flexDirection: "column",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Orbitron:wght@700&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #0f1525; }
        ::-webkit-scrollbar-thumb { background: #2a3a5c; border-radius: 3px; }
        .pixel-empty { transition: filter 0.12s; cursor: pointer; }
        .pixel-empty:hover { filter: brightness(2.8); }
        .pixel-selected { outline: 2px solid #5eead4 !important; outline-offset: -1px; background: rgba(94,234,212,0.14) !important; }
        .glow-premium { animation: premiumPulse 2s ease-in-out infinite; }
        @keyframes premiumPulse {
          0%,100% { box-shadow: 0 0 4px #ffd700, inset 0 0 4px rgba(255,215,0,0.15); }
          50% { box-shadow: 0 0 10px #ffd700, 0 0 20px rgba(255,215,0,0.3), inset 0 0 8px rgba(255,215,0,0.2); }
        }
        .panel-slide { animation: slideIn 0.25s ease; }
        @keyframes slideIn { from { transform: translateX(100%); opacity:0; } to { transform: translateX(0); opacity:1; } }
        input, select { background: #0f1525; border: 1px solid #1e3a5f; color: #e2e8f0; border-radius: 8px; padding: 8px 12px; width: 100%; font-size: 13px; outline: none; transition: border-color 0.2s; }
        input:focus, select:focus { border-color: #5eead4; }
        .btn-primary { background: linear-gradient(135deg, #5eead4, #14b8a6); color: #000; border: none; border-radius: 8px; padding: 10px 20px; font-weight: 700; font-size: 14px; cursor: pointer; width: 100%; transition: opacity 0.2s; }
        .btn-primary:hover { opacity: 0.9; }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-secondary { background: #1a2540; border: 1px solid #2a3a5c; color: #94a3b8; border-radius: 8px; padding: 8px 16px; font-size: 13px; cursor: pointer; }
        .btn-secondary:hover { background: #243050; color: #e2e8f0; }
        .social-btn { border-radius: 8px; padding: 8px 12px; cursor: pointer; border: 2px solid transparent; transition: all 0.15s; font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 6px; }
        .social-btn.active { border-color: #5eead4; background: rgba(94,234,212,0.1); }
        .social-btn:not(.active) { background: #1a2540; border-color: #2a3a5c; color: #94a3b8; }
        .rent-btn { border-radius: 6px; padding: 6px 10px; cursor: pointer; border: 1px solid #2a3a5c; background: #1a2540; color: #94a3b8; font-size: 12px; transition: all 0.15s; }
        .rent-btn.active { border-color: #5eead4; background: rgba(94,234,212,0.15); color: #5eead4; font-weight: 600; }
        .status-dot { width: 6px; height: 6px; border-radius: 50%; background: #22c55e; animation: blink 1.2s ease-in-out infinite; }
        @keyframes blink { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
        .grid-scanline { background-image: repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(94,234,212,0.04) 27px, rgba(94,234,212,0.04) 28px), repeating-linear-gradient(90deg, transparent, transparent 27px, rgba(94,234,212,0.04) 28px, rgba(94,234,212,0.04) 28px); }

        /* ─── Online users LED ─── */
        .online-indicator { display: flex; align-items: center; gap: 7px; font-size: 12px; color: #cbd5e1; font-weight: 600; background: rgba(15,21,37,0.7); border: 1px solid #1e3a5f; padding: 4px 10px; border-radius: 999px; }
        .online-led { width: 8px; height: 8px; border-radius: 50%; background: #22c55e; box-shadow: 0 0 6px #22c55e, 0 0 12px rgba(34,197,94,0.55); animation: ledPulse 1.6s ease-in-out infinite; }
        @keyframes ledPulse {
          0%   { transform: scale(1);   box-shadow: 0 0 6px #22c55e, 0 0 12px rgba(34,197,94,0.55); }
          50%  { transform: scale(1.45); box-shadow: 0 0 10px #22c55e, 0 0 22px rgba(34,197,94,0.85); }
          100% { transform: scale(1);   box-shadow: 0 0 6px #22c55e, 0 0 12px rgba(34,197,94,0.55); }
        }
        .online-count { color: #22c55e; font-family: 'Orbitron', monospace; letter-spacing: 0.5px; }

        /* Share button nel popout */
        .popout-share { background: rgba(94,234,212,0.12); color: #5eead4; border: 1px solid rgba(94,234,212,0.35); border-radius: 6px; padding: 6px 10px; font-size: 11px; font-weight: 700; cursor: pointer; width: 100%; text-align: center; letter-spacing: 0.4px; transition: background 0.15s, color 0.15s; }
        .popout-share:hover { background: rgba(94,234,212,0.22); }
        .popout-share.copied { background: rgba(34,197,94,0.18); color: #22c55e; border-color: rgba(34,197,94,0.5); }
        .popout-extend { background: rgba(255,215,0,0.10); color: #ffd700; border: 1px solid rgba(255,215,0,0.35); border-radius: 6px; padding: 6px 10px; font-size: 11px; font-weight: 700; cursor: pointer; text-align: center; letter-spacing: 0.3px; transition: background 0.15s; }
        .popout-extend:hover { background: rgba(255,215,0,0.22); }

        /* ─── Sparkle burst (Premium purchase celebration) ─── */
        .sparkle-core {
          position: absolute; left: -8px; top: -8px; width: 16px; height: 16px; border-radius: 50%;
          background: radial-gradient(circle, #fff 0%, #ffd700 40%, transparent 80%);
          animation: sparkFlash 0.55s ease-out forwards;
        }
        .sparkle-ring {
          position: absolute; left: -10px; top: -10px; width: 20px; height: 20px; border-radius: 50%;
          border: 2px solid #ffd700;
          box-shadow: 0 0 16px #ffd700, 0 0 28px rgba(255,215,0,0.6);
          animation: sparkRing 1.0s ease-out forwards;
        }
        .sparkle-particle {
          position: absolute; left: 0; top: 0; border-radius: 50%;
          background: radial-gradient(circle, #fff 0%, #ffd700 50%, #ff8a00 100%);
          box-shadow: 0 0 8px #ffd700, 0 0 14px rgba(255,200,0,0.85);
          transform: translate(-50%, -50%);
          animation: sparkParticle 1.2s cubic-bezier(0.2, 0.7, 0.3, 1) forwards;
        }
        @keyframes sparkFlash {
          0%   { transform: scale(0.6); opacity: 0.0; }
          25%  { transform: scale(2.5); opacity: 1; }
          100% { transform: scale(4.0); opacity: 0; }
        }
        @keyframes sparkRing {
          0%   { transform: scale(0.4); opacity: 1; }
          70%  { opacity: 0.85; }
          100% { transform: scale(6.5); opacity: 0; }
        }
        @keyframes sparkParticle {
          0%   { transform: translate(-50%, -50%) scale(0.4); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.2); opacity: 0; }
        }

        /* ─── FOMO Premium counter ─── */
        .fomo-bar { font-size: 12px; font-weight: 700; color: #cbd5e1; background: rgba(15,21,37,0.7); border: 1px solid #1e3a5f; padding: 6px 14px; border-radius: 999px; margin-bottom: 10px; letter-spacing: 0.4px; }
        .fomo-bar b { font-family: 'Orbitron', monospace; color: #ffd700; }
        .fomo-pulse { animation: fomoColorPulse 1.1s ease-in-out infinite; }
        @keyframes fomoColorPulse {
          0%, 100% { color: #fff; background: rgba(15,21,37,0.7); border-color: #1e3a5f; }
          50%      { color: #ff3b3b; background: rgba(127,29,29,0.35); border-color: #ef4444; }
        }
        .fomo-pulse b { animation: fomoColorPulse 1.1s ease-in-out infinite; }

        /* ─────────  RESPONSIVE / MOBILE  ───────── */
        .live-sidebar { width: 220px; border-left: 1px solid #1e3a5f; }
        .header-meta-desktop { display: flex; align-items: center; gap: 16px; }
        .header-meta-mobile { display: none; }
        .legend-row { display: flex; gap: 20px; flex-wrap: wrap; justify-content: center; }
        .buy-panel-aside { width: 380px; }
        .zoom-stage { height: min(80vh, calc(100vh - 140px)); min-height: 460px; }

        @media (max-width: 760px) {
          .live-sidebar { display: none !important; }
          .header-meta-desktop { display: none; }
          .header-meta-mobile { display: flex; align-items: center; gap: 8px; font-size: 11px; }
          .app-header-inner { padding: 8px 12px !important; }
          .brand-name { font-size: 13px !important; }
          .brand-icon { width: 26px !important; height: 26px !important; font-size: 14px !important; }
          .header-claim { display: none !important; }
          .brand-logo-img { height: 36px !important; width: auto !important; object-fit: contain !important; flex-shrink: 0 !important; }
          .grid-outer-wrap { padding: 6px !important; justify-content: center !important; }
          .legend-row { font-size: 10px; gap: 12px; }
          .legend-row .legend-hide-mobile { display: none; }
          .buy-panel-aside { width: 100vw !important; max-width: 100vw !important; }
          .main-area { padding: 8px !important; }
          .zoom-stage { height: min(78vh, calc(100vh - 200px)) !important; border-radius: 6px !important; }
          .selection-bar { padding: 8px 10px !important; gap: 8px !important; flex-wrap: wrap; }
          .selection-bar span:nth-child(2) { display: none; }
        }
        /* Disabilita gesture browser native sulla stage (no zoom/pinch nativo, no scroll) */
        .zoom-stage { touch-action: none; }
      `}</style>

      {/* Header */}
      <header data-testid="app-header" className="app-header-inner" style={{ borderBottom: "1px solid #1e3a5f", padding: "12px 24px", display: "flex", alignItems: "center", gap: 16, background: "#080b14", position: "sticky", top: 0, zIndex: 100 }}>
        <a
          data-testid="brand-home-link"
          href="/"
          aria-label="LiveGrid — vai alla home"
          style={{ display: "inline-flex", alignItems: "center", textDecoration: "none", lineHeight: 0, flexShrink: 0 }}
        >
          <img
            src="/livegrid-logo.jpeg"
            alt="LiveGrid"
            className="brand-logo-img"
            style={{ height: 40, width: "auto", borderRadius: 8, display: "block", objectFit: "contain", flexShrink: 0 }}
          />
        </a>
        <div
          className="header-claim"
          data-testid="header-claim"
          style={{
            flex: 1,
            textAlign: "center",
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "0.5px",
            color: "#5eead4",
            textShadow: "0 0 12px rgba(94,234,212,0.25)",
            userSelect: "none",
          }}
        >
          The Live Pixel Wall
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <OnlineIndicator />
          <div className="header-meta-desktop" style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#94a3b8" }}>
              <div className="status-dot" />
              <span data-testid="live-count-label">LIVE • {liveCount} pixel occupati</span>
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              <span style={{ color: "#ffd700" }}>●</span> Premium (centro)
              <span style={{ marginLeft: 10, color: "#475569" }}>●</span> Standard (bordi)
            </div>
          </div>
        </div>
      </header>

      {successMsg && (
        <div data-testid="success-toast" style={{ position: "fixed", top: 70, left: "50%", transform: "translateX(-50%)", background: "#052e16", border: "1px solid #166534", color: "#4ade80", borderRadius: 10, padding: "10px 24px", fontSize: 14, fontWeight: 600, zIndex: 400, boxShadow: "0 4px 20px rgba(0,0,0,0.5)", maxWidth: "calc(100vw - 24px)" }}>
          {successMsg}
        </div>
      )}

      {errorMsg && (
        <div data-testid="error-toast" style={{ position: "fixed", top: 70, left: "50%", transform: "translateX(-50%)", background: "#2e0505", border: "1px solid #7f1d1d", color: "#fca5a5", borderRadius: 10, padding: "10px 24px", fontSize: 13, fontWeight: 600, zIndex: 400, boxShadow: "0 4px 20px rgba(0,0,0,0.5)", maxWidth: "min(600px, calc(100vw - 24px))" }}>
          {errorMsg}
          <button onClick={() => setErrorMsg("")} style={{ marginLeft: 12, background: "transparent", border: "none", color: "#fca5a5", cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
      )}

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <main className="main-area" style={{ flex: 1, overflow: "auto", padding: 16, display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0 }}>
          {loading && (
            <div data-testid="loading-state" style={{ marginBottom: 12, padding: 12, color: "#64748b", fontSize: 13 }}>Caricamento pixel da Supabase…</div>
          )}

          {/* FOMO Premium counter (sopra la griglia) */}
          <div data-testid="fomo-premium" className={`fomo-bar${premiumAvailable < 10 ? " fomo-pulse" : ""}`}>
            ⭐ Spot Premium disponibili: <b data-testid="fomo-count">{premiumAvailable}</b> / 36
          </div>

          {selected.length > 0 && (
            <div className="selection-bar" style={{ marginBottom: 12, background: "#0f1525", border: bboxInfo.conflicts.length > 0 ? "1px solid #ef4444" : "1px solid #1e3a5f", borderRadius: 10, padding: "10px 16px", display: "flex", alignItems: "center", gap: 12, width: "100%", maxWidth: GRID_COLS * cellSize + 4 }}>
              <span style={{ fontSize: 13, color: "#94a3b8" }}>
                <span style={{ color: "#5eead4", fontWeight: 700 }} data-testid="selected-count">{bboxInfo.keys.length}</span> px · blocco <b style={{ color: "#fff" }}>{bboxInfo.rs}×{bboxInfo.cs}</b>
                {selectedPremium > 0 && <span style={{ color: "#ffd700", marginLeft: 6 }}>({selectedPremium}⭐)</span>}
                {bboxInfo.conflicts.length > 0 && <span style={{ color: "#ef4444", marginLeft: 6 }}>· {bboxInfo.conflicts.length} occupati ⚠</span>}
              </span>
              <span style={{ fontSize: 12, color: "#facc15", fontWeight: 600 }}>da €{pricePerHour.toFixed(2)} / 5h</span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button data-testid="clear-selection-btn" className="btn-secondary" style={{ padding: "6px 12px", fontSize: 12 }} onClick={() => setSelected([])}>Cancella</button>
                <button data-testid="open-buy-panel-btn" className="btn-primary" disabled={bboxInfo.conflicts.length > 0} style={{ width: "auto", padding: "6px 14px", fontSize: 12, opacity: bboxInfo.conflicts.length > 0 ? 0.5 : 1 }} onClick={openBuyPanel}>
                  Affitta ora →
                </button>
              </div>
            </div>
          )}

          <div data-testid="zoom-stage" className="zoom-stage" ref={stageRef}
            onMouseDown={onStageMouseDown}
            onMouseMove={onStageMouseMove}
            onMouseUp={onStageMouseUp}
            onMouseLeave={onStageMouseUp}
            style={{
            position: "relative",
            width: "100%",
            background: "#070a12",
            border: "1px solid #1e3a5f",
            borderRadius: 12,
            overflow: "hidden",
          }}>
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={6}
              centerOnInit
              limitToBounds={false}
              smooth={false}
              wheel={{
                step: 0.06,            // sensibilità rotella ridotta (default 0.2)
                smoothStep: 0.001,
                disabled: false,
                wheelDisabled: false,
                touchPadDisabled: false,
              }}
              pinch={{ step: 5 }}
              doubleClick={{ disabled: false, mode: "zoomIn", step: 0.5 }}
              panning={{
                velocityDisabled: false,
                disabled: false,
                allowLeftClickPan: true,
                allowMiddleClickPan: true,
                allowRightClickPan: false,
                activationKeys: [],
              }}
            >
              <ZoomControls />
              <AutoFocusToPixel targetKey={autoOpenKey} onDone={() => { /* OccupiedPixel finirà l'apertura */ }} />
              <AutoFitGrid autoOpenKey={autoOpenKey} />
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%", background: "#070a12" }}
                contentStyle={{ width: "100%", height: "100%" }}
              >
                <div className="grid-outer-wrap" style={{ padding: 24, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100%" }}>
                  <CellSizeContext.Provider value={cellSize}>
                  <div
                    data-testid="pixel-grid"
                    className="grid-scanline"
                    style={{
                      display: "grid",
                      gridTemplateColumns: `repeat(${GRID_COLS}, ${cellSize}px)`,
                      gridTemplateRows: `repeat(${GRID_ROWS}, ${cellSize}px)`,
                      gap: 1, background: "#0d1420", border: "1px solid #1e3a5f", borderRadius: 8, overflow: "hidden",
                      position: "relative",
                      margin: "0 auto",
                    }}
                  >
                    {Array.from({ length: GRID_ROWS }, (_, row) =>
                      Array.from({ length: GRID_COLS }, (_, col) => {
                        const key = `${row}-${col}`;
                        const val = grid[key];
                        const premium = isPremium(row, col);

                        if (val?.ref) return null;

                        if (val && !val.ref) {
                          return <OccupiedPixel key={key} data={val} row={row} col={col} premium={premium} autoOpenKey={autoOpenKey} onAutoOpenConsumed={() => setAutoOpenKey(null)} grid={grid} onExtend={handleExtendBlock} />;
                        }

                        const sel = selected.includes(key);
                        const inBbox = !sel && bboxInfo.keys.includes(key);
                        return (
                          <div
                            key={key}
                            data-testid={`empty-pixel-${row}-${col}`}
                            className={`pixel-empty${sel ? " pixel-selected" : ""}${inBbox ? " pixel-bbox" : ""}`}
                            onClick={() => handleEmptyClick(row, col)}
                            onMouseEnter={() => setHoveredKey(key)}
                            onMouseLeave={() => setHoveredKey(null)}
                            style={{
                              gridRow: row + 1, gridColumn: col + 1, width: cellSize, height: cellSize,
                              background: sel ? "rgba(94,234,212,0.18)" : inBbox ? "rgba(94,234,212,0.08)" : premium ? "rgba(255,215,0,0.06)" : "#0d1420",
                              border: sel ? "1px solid #5eead4" : inBbox ? "1px dashed rgba(94,234,212,0.45)" : premium ? "1px solid rgba(255,215,0,0.28)" : "1px solid #121d2e",
                              position: "relative", display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                            title={`${row},${col} · ${premium ? "PREMIUM" : "Standard"} · da €${(premium ? PRICING.premium : PRICING.standard).hour.toFixed(2)} ogni 5h`}
                          >
                            {hoveredKey === key && (
                              <span style={{ fontSize: 7, color: premium ? "#ffd700" : "#475569", fontWeight: 600, pointerEvents: "none" }}>
                                €{(premium ? PRICING.premium : PRICING.standard).hour.toFixed(2)}
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                    {/* Sparkle bursts su acquisti Premium */}
                    {sparkles.map((s) => (
                      <SparkleBurst key={s.id} x={s.x} y={s.y} />
                    ))}
                  </div>
                  </CellSizeContext.Provider>
                </div>
              </TransformComponent>
            </TransformWrapper>
          </div>

          <div className="legend-row" style={{ marginTop: 12, fontSize: 11, color: "#64748b" }}>
            <span><span style={{ color: "#5eead4" }}>◻</span> Tocca/clicca pixel liberi</span>
            <span><span style={{ color: "#ffd700" }}>◻</span> Premium (6×6 centro) · da €{PRICING.premium.hour.toFixed(2)}/px ogni 5h</span>
            <span><span style={{ color: "#475569" }}>◻</span> Standard · da €{PRICING.standard.hour.toFixed(2)}/px ogni 5h</span>
            <span className="legend-hide-mobile">🖱 Hover/tap occupati → anteprima · Drag per spostare · Rotella/pinch zoom</span>
          </div>
        </main>

        <aside data-testid="live-sidebar" className="live-sidebar" style={{ background: "#080b14", padding: 12, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", letterSpacing: 1, marginBottom: 4 }}>LIVE ORA</div>
          {Object.entries(grid)
            .filter(([, v]) => v && !v.ref)
            .map(([key, v]) => {
              const social = SOCIALS[v.social_type] || SOCIALS.link;
              const tl = getTimeLeft(v.rent_start, v.rent_duration_hours);
              const info = getMediaEmbed(v.live_link, v.social_type);
              const thumbSrc = info.thumbnail || v.media_url || info.favicon;
              return (
                <div key={key} style={{ background: "#0f1525", borderRadius: 8, padding: 8, border: "1px solid #1e3a5f", display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ width: 32, height: 32, borderRadius: 6, overflow: "hidden", flexShrink: 0 }}>
                    {thumbSrc ? (
                      <img src={thumbSrc} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", background: social.color }} />
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.user_handle}</div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>{social.label}</div>
                    <div style={{ fontSize: 9, color: tl < 1800000 ? "#ef4444" : "#64748b", fontFamily: "monospace" }}>{formatTime(tl)}</div>
                  </div>
                </div>
              );
            })}
          {liveCount === 0 && !loading && (
            <div style={{ fontSize: 12, color: "#374151", textAlign: "center", padding: 16 }}>Nessuna live attiva</div>
          )}
        </aside>
      </div>

      {/* Buy Panel */}
      {panel === "buy" && (
        <div data-testid="buy-panel" style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex" }}>
          <div style={{ flex: 1, background: "rgba(0,0,0,0.6)" }} onClick={() => setPanel(null)} />
          <div className="panel-slide buy-panel-aside" style={{ background: "#080b14", borderLeft: "1px solid #1e3a5f", overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontFamily: "'Orbitron', monospace", fontSize: 14, color: "#5eead4" }}>AFFITTA PIXEL</span>
              <button data-testid="close-buy-panel-btn" className="btn-secondary" style={{ width: "auto", padding: "4px 10px" }} onClick={() => setPanel(null)}>✕</button>
            </div>

            <div style={{ background: "#0f1525", borderRadius: 8, padding: 12, border: "1px solid #1e3a5f" }}>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Pixel selezionati: <span style={{ color: "#5eead4", fontWeight: 700 }}>{selected.length}</span></div>
              <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                {selectedStandard > 0 && <span style={{ color: "#64748b" }}>{selectedStandard} standard</span>}
                {selectedPremium > 0 && <span style={{ color: "#ffd700" }}>{selectedPremium} premium ⭐</span>}
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 8 }}>Piattaforma Social</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {Object.entries(SOCIALS).map(([k, s]) => (
                  <button key={k} data-testid={`social-btn-${k}`} className={`social-btn${form.social_type === k ? " active" : ""}`} onClick={() => setForm((f) => ({ ...f, social_type: k }))}>
                    <span style={{ width: 16, height: 16, borderRadius: 4, background: s.gradient || s.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{s.svg}</span>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 6 }}>Nome / Handle</label>
              <input data-testid="user-handle-input" placeholder="@tuonome" value={form.user_handle} onChange={(e) => setForm((f) => ({ ...f, user_handle: e.target.value }))} />
            </div>

            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 6 }}>Link alla Live / Canale</label>
              <input data-testid="live-link-input" placeholder="https://..." value={form.live_link} onChange={(e) => setForm((f) => ({ ...f, live_link: e.target.value }))} />
              {embedPreview && embedPreview.kind === "iframe" && (
                <div style={{ fontSize: 10, color: "#22c55e", marginTop: 4 }}>✓ Player {embedPreview.providerLabel} embeddabile — anteprima video on tap/hover</div>
              )}
              {embedPreview && embedPreview.kind === "card" && form.live_link && (
                <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>ℹ {embedPreview.providerLabel} non supporta l&apos;embed video — verrà mostrata una scheda con favicon + link</div>
              )}
            </div>

            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 6 }}>URL Immagine / GIF anteprima</label>
              <input data-testid="media-url-input" placeholder="https://... (opzionale)" value={form.media_url} onChange={(e) => setForm((f) => ({ ...f, media_url: e.target.value }))} />
              <div style={{ fontSize: 10, color: "#475569", marginTop: 4 }}>Lascia vuoto per immagine demo (o thumbnail YouTube automatica)</div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 8 }}>Durata prevista</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {DURATIONS.map((d) => {
                  const stdP = PRICING.standard[d.key];
                  const prmP = PRICING.premium[d.key];
                  // Prezzo "non scontato" = quanto costerebbe comprando N blocchi da 5h
                  const units = d.hours / BASELINE_HOURS;
                  const baselineStd = units * PRICING.standard.hour;
                  const baselinePrm = units * PRICING.premium.hour;
                  const stdDiscounted = baselineStd > stdP + 0.001;
                  const prmDiscounted = baselinePrm > prmP + 0.001;
                  // Mostra solo la categoria realmente presente nella selezione.
                  // Se la selezione è "mista" (raro: bbox che attraversa il bordo Premium), mostra entrambe.
                  const showStd = selectedStandard > 0;
                  const showPrm = selectedPremium > 0;
                  return (
                    <button
                      key={d.key}
                      data-testid={`rent-btn-${d.key}`}
                      className={`rent-btn${form.rent_tier === d.key ? " active" : ""}`}
                      onClick={() => setForm((f) => ({ ...f, rent_tier: d.key }))}
                      style={{ textAlign: "left", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}
                    >
                      <div style={{ fontWeight: 700, fontSize: 12, color: "#e2e8f0" }}>{d.label}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {showStd && (
                          <div data-testid={`rent-price-std-${d.key}`} style={{ display: "flex", alignItems: "baseline", gap: 6, lineHeight: 1.1 }}>
                            {stdDiscounted && (
                              <span style={{ color: "#94a3b8", opacity: 0.55, fontSize: 10, textDecoration: "line-through", textDecorationThickness: "1.5px" }}>
                                €{baselineStd.toFixed(2)}
                              </span>
                            )}
                            <span style={{ color: "#facc15", fontSize: 14, fontWeight: 800, letterSpacing: "0.3px" }}>
                              €{stdP.toFixed(2)}
                            </span>
                            {showPrm && (
                              <span style={{ color: "#475569", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.5px" }}>std</span>
                            )}
                          </div>
                        )}
                        {showPrm && (
                          <div data-testid={`rent-price-prm-${d.key}`} style={{ display: "flex", alignItems: "baseline", gap: 6, lineHeight: 1.1 }}>
                            {prmDiscounted && (
                              <span style={{ color: "#94a3b8", opacity: 0.55, fontSize: 10, textDecoration: "line-through", textDecorationThickness: "1.5px" }}>
                                €{baselinePrm.toFixed(2)}
                              </span>
                            )}
                            <span style={{ color: "#fbbf24", fontSize: 14, fontWeight: 800, letterSpacing: "0.3px" }}>
                              €{prmP.toFixed(2)}
                            </span>
                            {showStd && (
                              <span style={{ color: "#475569", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.5px" }}>prm</span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ background: "#0f1525", borderRadius: 10, padding: 14, border: "1px solid #1e3a5f" }}>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, letterSpacing: "0.5px" }}>RIEPILOGO PREZZO · {tierMeta.label}</div>
              {selectedStandard > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>
                  <span>{selectedStandard} px standard × €{tierPriceStandard.toFixed(2)}</span>
                  <span>€{(selectedStandard * tierPriceStandard).toFixed(2)}</span>
                </div>
              )}
              {selectedPremium > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#fbbf24", marginBottom: 4 }}>
                  <span>{selectedPremium} px premium ⭐ × €{tierPricePremium.toFixed(2)}</span>
                  <span>€{(selectedPremium * tierPricePremium).toFixed(2)}</span>
                </div>
              )}
              {(() => {
                const units = tierMeta.hours / BASELINE_HOURS;
                const baselineTotal = (selectedStandard * PRICING.standard.hour + selectedPremium * PRICING.premium.hour) * units;
                const showDiscount = baselineTotal > totalPriceNum + 0.005;
                const savings = baselineTotal - totalPriceNum;
                return (
                  <>
                    <div style={{ borderTop: "1px solid #1e3a5f", marginTop: 8, paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0", letterSpacing: "0.5px" }}>TOTALE</span>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {showDiscount && (
                          <span style={{ fontSize: 14, color: "#94a3b8", opacity: 0.55, textDecoration: "line-through", textDecorationThickness: "2px" }}>
                            €{baselineTotal.toFixed(2)}
                          </span>
                        )}
                        <span style={{ fontSize: 22, fontWeight: 800, color: "#facc15", fontFamily: "'Orbitron', monospace", letterSpacing: "0.5px", textShadow: "0 0 12px rgba(250,204,21,0.35)" }} data-testid="total-price">
                          €{totalPrice}
                        </span>
                      </div>
                    </div>
                    {showDiscount && (
                      <div style={{ fontSize: 10, color: "#4ade80", textAlign: "right", marginTop: 2, fontWeight: 600 }} data-testid="savings-line">
                        Risparmi €{savings.toFixed(2)} ({Math.round((savings / baselineTotal) * 100)}%)
                      </div>
                    )}
                  </>
                );
              })()}
              <div style={{ fontSize: 10, color: "#475569", marginTop: 6 }}>Scade dopo {tierMeta.label.toLowerCase()} dal momento dell&apos;acquisto</div>
            </div>

            <button data-testid="confirm-buy-btn" className="btn-primary" disabled={buying || !form.user_handle || !form.live_link || totalPriceNum <= 0} onClick={handleBuy}>
              {buying ? "Apertura Stripe…" : `💳 Paga con Stripe · €${totalPrice}`}
            </button>
            <div style={{ fontSize: 10, color: "#475569", textAlign: "center" }}>
              Pagamento sicuro tramite Stripe · I pixel saranno attivati appena confermato
            </div>

            <div style={{ fontSize: 10, color: "#374151", textAlign: "center" }}>
              {useSupabase
                ? "Persistenza Supabase attiva · Realtime abilitato"
                : "Demo mode — Supabase non configurato"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
